import { Splide, SplideTrack, SplideSlide } from '@splidejs/react-splide';
import Button from 'components/ui-components/Button';

import ProductPricing from 'components/products/product_pricing';
import { formatPricingParams, setGtmClickAttr } from 'lib/utils';
import { useGET } from 'hooks/useRequest';

import Arrow from '../../../assets/images/shared/icons/arrow-right-grey.svg';

/**
 * @param {Object} props
 * @param {Array} props.products - list of product data to display
 * @param {string} props.label - aria label for the carousel
 * @param {boolean} props.vatStatus - VAT status to show inclusive/exclusive VAT pricing
 * @returns related product carousel
 */
const RelatedProductsCarousel = ({ products, label, vatStatus, options }) => {
  const CAROUSEL_OPTIONS = {
    perPage: 5,
    gap: '0.75rem',
    flickMaxPages: 1,
    arrows: products.length > 5,
    drag: products.length > 5,
    rewind: true,
    breakpoints: {
      // mobile
      767: {
        arrows: false,
        rewind: false,
        perPage: 2,
        drag: products.length > 2,
        padding: { right: '20%' },
      },
      // tablet
      1024: {
        arrows: false,
        rewind: false,
        perPage: 3,
        drag: products.length > 3,
        padding: { right: '10%' },
      },
      // small desktop
      1119: {
        arrows: products.length > 4,
        drag: products.length > 4,
        perPage: 4,
      },
    },
    ...options,
  };

  const handlePagination = (_i, data) => {
    for (let i = 0; i < data.items.length; i++) {
      setGtmClickAttr(data.items[i].button);
    }
  };

  // fetching product prices
  const { data: productPrices } = useGET(formatPricingParams(products));

  return (
    <Splide
      options={CAROUSEL_OPTIONS}
      aria-label={label}
      hasTrack={false}
      className="related-product-carousel"
      onPaginationMounted={handlePagination}>
      <div className="splide__arrows">
        <Button className="splide__arrow splide__arrow--prev">
          <img
            alt="previous"
            src={Arrow}
            width="20"
            height="30"
            loading="lazy"
          />
        </Button>
        <Button className="splide__arrow splide__arrow--next">
          <img alt="next" src={Arrow} width="20" height="30" loading="lazy" />
        </Button>
      </div>
      <div className="container">
        <SplideTrack className="no-margin">
          {products.map(
            ({
              step_id: stepId,
              product_image: image,
              brand_logo: brandLogo,
              brand_name: brand,
              description_short: description,
              page_slug: slug,
            }) => (
              <SplideSlide
                className="related-product-carousel__wrapper splide__slide"
                key={stepId}>
                <a
                  href={`/catalogue/products/${slug}`}
                  className="related-product-carousel__content"
                  data-gtm-click="true">
                  <div className="fl fl-col">
                    <div className="related-product-carousel__brand-wrapper">
                      {brandLogo && (
                        <img
                          src={brandLogo}
                          alt={brand || ''}
                          width="80"
                          height="30"
                          loading="lazy"
                        />
                      )}
                    </div>
                    <img
                      src={image}
                      alt=""
                      className="related-product-carousel__image"
                      width="140"
                      height="140"
                      loading="lazy"
                    />
                  </div>

                  <p
                    className="related-product-carousel__description
                               tc-grey-70 lh-1-1 mt-8 l-fs-16 mb-16">
                    {description}
                  </p>

                  <ProductPricing
                    id={stepId}
                    prices={productPrices}
                    vatStatus={vatStatus}
                  />
                </a>
              </SplideSlide>
            ),
          )}
        </SplideTrack>
      </div>
    </Splide>
  );
};

export default RelatedProductsCarousel;
